import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface HCCupStandingsProps {
  members: any[];
  selectedTournament?: any;
  year?: string;
  searchTerm: string; 
}

interface Standing {
  player_name: string;
  current_status: string;
  player_season: any;
  points_2024: number;
  points_2025: number;
  rounds_2024: number;
  rounds_2025: number;
  scores_2024: number[];
  scores_2025: number[];
}

export function HCCupStandings({ members, selectedTournament, year = '2025', searchTerm }: HCCupStandingsProps) {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return;
    }

    setIsLoading(true);

    // Load initial standings
    const loadStandings = async () => {
      try {
        const { data: standings, error: standingsError } = await supabase
          .from('season_total_points')
          .select('*')
          .order(`points_${year}`, { ascending: false, nullsLast: true })
          .order('player_name', { ascending: true });

        if (standingsError) throw standingsError;
        setStandings(standings || []);
        setError(null);
      } catch (err) {
        console.error('Error loading standings:', err);
        setError('Failed to load standings');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial load
    loadStandings();

    // Subscribe to changes
    const subscription = supabase
      .channel('hc-cup-standings')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'tournament_points' },
        async () => {
          try {
            await loadStandings();
            setIsSubscribed(true);
          } catch (err) {
            console.error('Error processing standings update:', err);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsSubscribed(true);
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [searchTerm, year]);

  if (members.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Trophy className="w-6 h-6" />
          HC Cup Standings
        </h2>
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Members Found
          </h3>
          <p className="text-gray-600 max-w-sm mx-auto">
            There are currently no members in the database. Please add members to view HC Cup standings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="text-gray-600">Loading standings...</p>
          </div>
        </div>
      )}
      {error && (
        <div className="text-center p-4 text-red-600">
          <p>{error}</p>
        </div>
      )}
      <div className="overflow-x-auto relative max-h-[calc(100vh-12rem)] scrolling-touch">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-[9px] sm:text-[10px] leading-tight">
              <th className="w-8 px-1 py-2"></th>
              <th 
                className="px-2 py-1 text-left font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
              >
                Name
              </th>
              <th className="px-0.5 py-1 text-center font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Status
              </th>
              <th className="px-0.5 py-1 text-right font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                <div>HC</div>
                <div>Total</div>
                <div>pts</div>
              </th>
              <th className="px-0.5 py-1 text-center font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                <div>Events</div>
                <div>{year === '2024' ? 'Feb-Jul' : 'Nov-Jul'}</div>
              </th>
              <th className="px-0 py-1 text-center font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Scores
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {standings
              .filter(standing => 
                standing.player_name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((standing, index) => (
              <tr key={standing.player_name} className="hover:bg-gray-50">
                <td className="w-6 px-0.5 py-2 whitespace-nowrap text-xs text-center text-gray-500">
                  {index + 1}
                </td>
                <td className="px-0.5 py-2 text-xs font-medium text-gray-900"> 
                  <div className="leading-tight">
                    {standing.player_name.split(' ').map((part, i) => (
                      <div key={i}>{part}</div>
                    ))}
                  </div>
                </td>
                <td className="px-0.5 py-2 whitespace-nowrap text-[9px] text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    standing.current_status === 'M' 
                      ? 'bg-green-100 text-green-800'
                      : standing.current_status === 'NVM'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {standing.current_status === 'M' ? 'Member' : 
                     standing.current_status === 'NVM' ? 'NVM' : 
                     'Inactive'}
                  </span>
                </td>
                <td className="px-0.5 py-2 whitespace-nowrap text-xs text-right font-bold text-red-600">
                  {standing[`points_${year}`] || 0}
                </td>
                <td className="px-0.5 py-2 whitespace-nowrap text-xs text-center">
                  {standing[`rounds_${year}`] || 0}
                </td>
                <td className="px-0.5 py-2 whitespace-nowrap text-xs text-left pl-4">
                  {standing[`scores_${year}`]
                    ?.map(score => score)
                    .join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}